import { FC, useMemo, useState } from "react";
import classNames from "classnames";
// @ts-ignore
import { FormattedNumber } from "@umijs/max";
import dayjs from "dayjs";

import BasicIcon from "../../assets/icons/basic.svg";
import PlusIcon from "../../assets/icons/plus.svg";
import ProIcon from "../../assets/icons/pro.svg";
import UltraIcon from "../../assets/icons/ultra.svg";
import CustomIcon from "../../assets/icons/custom.svg";

import { ReactComponent as InfiniteIcon } from "../../assets/svg/infinite.svg";

import styles from "./index.less";

import { isPredefinedVersion, PricingPlanVersionEnum } from "../../types";

const PlanVersionTextAndIconMap: Record<
  PricingPlanVersionEnum,
  { text: string; icon: React.ReactNode }
> = {
  [PricingPlanVersionEnum.BASIC]: {
    text: "Basic",
    icon: <img src={BasicIcon} alt="basic" className={styles.icon} />,
  },
  [PricingPlanVersionEnum.PLUS]: {
    text: "Plus",
    icon: <img src={PlusIcon} alt="plus" className={styles.icon} />,
  },
  [PricingPlanVersionEnum.PRO]: {
    text: "Pro",
    icon: <img src={ProIcon} alt="pro" className={styles.icon} />,
  },
  [PricingPlanVersionEnum.ULTRA]: {
    text: "Ultra",
    icon: <img src={UltraIcon} alt="ultra" className={styles.icon} />,
  },
};

interface UserPlanResourceCardProps {
  /** 到期时间 */
  expireAt?: string;
  /** 是否显示到期提示 */
  showExpireNotice?: boolean;
  /** 已付费的版本 */
  paidVersion: PricingPlanVersionEnum;
  /** 是否是试用计划 */
  trailPlan: boolean;
  /** 剩余席位 */
  availableSeat?: number;
  /** 剩余机器人 */
  availableRobot?: number;
  /** 剩余动能值 */
  availablePower?: number;
  /** 购买席位事件 */
  onBuySeat: () => void;
  /** 购买机器人事件 */
  onBuyRobot: () => void;
  /** 购买动能值事件 */
  onBuyPower: () => void;
  /** 续费事件 */
  onRenew: () => void;
  /** 续费自定义套餐事件 */
  onRenewCustomPlan: () => void;
  /** 更改订阅事件 */
  onChangeSubscription: () => void;
}

/**
 * 用户费用信息卡片
 * @returns
 */
const UserPlanResourceCard: FC<UserPlanResourceCardProps> = ({
  expireAt,
  showExpireNotice,
  paidVersion,
  trailPlan,
  availableSeat,
  availableRobot,
  availablePower,
  onBuySeat,
  onBuyRobot,
  onBuyPower,
  onRenew,
  onRenewCustomPlan,
  onChangeSubscription,
}) => {
  const [showBuySeat, setShowBuySeat] = useState<boolean>(false);
  const [showBuyRobot, setShowBuyRobot] = useState<boolean>(false);
  const [showBuyPower, setShowBuyPower] = useState<boolean>(false);

  const diffDay = dayjs(expireAt).diff(dayjs(), "day");

  const paidVersionMaterial = useMemo(() => {
    if (isPredefinedVersion(paidVersion)) {
      return PlanVersionTextAndIconMap[paidVersion];
    }

    return {
      text: paidVersion,
      icon: <img src={CustomIcon} alt="custom" className={styles.icon} />,
    };
  }, [paidVersion]);

  return (
    <div
      className={classNames(styles.container, {
        [styles["container-basic"]]:
          paidVersion === PricingPlanVersionEnum.BASIC,
        [styles["container-plus"]]: paidVersion === PricingPlanVersionEnum.PLUS,
        [styles["container-pro"]]: paidVersion === PricingPlanVersionEnum.PRO,
        [styles["container-ultra"]]:
          paidVersion === PricingPlanVersionEnum.ULTRA,
        [styles["container-custom"]]: !isPredefinedVersion(paidVersion),
      })}
    >
      <div className={styles["plan-info"]}>
        <div
          className={classNames(styles.version, {
            [styles["version-basic"]]:
              paidVersion === PricingPlanVersionEnum.BASIC,
            [styles["version-plus"]]:
              paidVersion === PricingPlanVersionEnum.PLUS,
            [styles["version-pro"]]: paidVersion === PricingPlanVersionEnum.PRO,
            [styles["version-ultra"]]:
              paidVersion === PricingPlanVersionEnum.ULTRA,
            [styles["version-custom"]]: !isPredefinedVersion(paidVersion),
          })}
        >
          {paidVersionMaterial.icon}
          {paidVersionMaterial.text}
        </div>

        {!showExpireNotice && (
          <div
            className={classNames(styles["expired-with-date"], {
              [styles["expired-with-date-basic"]]:
                paidVersion === PricingPlanVersionEnum.BASIC,
              [styles["expired-with-date-plus"]]:
                paidVersion === PricingPlanVersionEnum.PLUS,
              [styles["expired-with-date-pro"]]:
                paidVersion === PricingPlanVersionEnum.PRO,
              [styles["expired-with-date-ultra"]]:
                paidVersion === PricingPlanVersionEnum.ULTRA,
              [styles["expired-with-date-custom"]]:
                !isPredefinedVersion(paidVersion),
            })}
          >
            {expireAt} 到期
          </div>
        )}

        {showExpireNotice && diffDay <= 7 && diffDay >= 0 && (
          <div className={styles["expired-less-than-7-days"]}>
            {diffDay}天后到期
          </div>
        )}

        {showExpireNotice && diffDay < 0 && (
          <div className={styles["expired-after"]}>
            已过期{Math.abs(diffDay)}天
          </div>
        )}
      </div>
      <div className={styles["resource-info"]}>
        <div
          className={styles.item}
          onMouseEnter={() => {
            setShowBuySeat(true);
          }}
          onMouseLeave={() => {
            setShowBuySeat(false);
          }}
        >
          {showBuySeat ? (
            <div className={styles["buy-item"]} onClick={onBuySeat}>
              购买席位
            </div>
          ) : (
            <>
              <div className={styles.num}>
                {availableSeat !== void 0 ? (
                  <FormattedNumber value={availableSeat} />
                ) : (
                  "--"
                )}
              </div>
              <div className={styles.desc}>剩余席位</div>
            </>
          )}
        </div>

        <div
          className={styles.item}
          onMouseEnter={() => {
            setShowBuyRobot(true);
          }}
          onMouseLeave={() => {
            setShowBuyRobot(false);
          }}
        >
          {paidVersion === PricingPlanVersionEnum.ULTRA && (
            <>
              <InfiniteIcon />
              <div className={styles.desc}>无限机器人</div>
            </>
          )}

          {paidVersion !== PricingPlanVersionEnum.ULTRA && (
            <>
              {isPredefinedVersion(paidVersion) && showBuyRobot ? (
                <div className={styles["buy-item"]} onClick={onBuyRobot}>
                  升级套餐解
                  <br />
                  锁更多额度
                </div>
              ) : (
                <>
                  <div className={styles.num}>
                    {availableRobot !== void 0 ? (
                      <FormattedNumber value={availableRobot} />
                    ) : (
                      "--"
                    )}
                  </div>
                  <div className={styles.desc}>剩余机器人</div>
                </>
              )}
            </>
          )}
        </div>

        <div
          className={styles.item}
          onMouseEnter={() => {
            setShowBuyPower(true);
          }}
          onMouseLeave={() => {
            setShowBuyPower(false);
          }}
        >
          {showBuyPower ? (
            <div className={styles["buy-item"]} onClick={onBuyPower}>
              购买动能值
            </div>
          ) : (
            <>
              <div className={styles.num}>
                {availablePower !== void 0 ? (
                  <FormattedNumber value={availablePower} />
                ) : (
                  "--"
                )}
              </div>
              <div className={styles.desc}>剩余动能值</div>
            </>
          )}
        </div>
      </div>

      {!trailPlan && (
        <div className={styles.actions}>
          <div
            className={styles["action-renew"]}
            onClick={
              isPredefinedVersion(paidVersion) ? onRenew : onRenewCustomPlan
            }
          >
            续费
          </div>

          {isPredefinedVersion(paidVersion) && (
            <div
              className={styles["action-change"]}
              onClick={onChangeSubscription}
            >
              更改订阅
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserPlanResourceCard;
